import React, { useState, useEffect, useCallback } from 'react';
import alphabetData from '../Data/alphabetData';
import Confetti from 'react-confetti';
import { useDrag, useDrop } from 'react-dnd';
import { shuffle } from '../utils/shuffle';
import { getRandomMascotQuote } from '../utils/mascotQuotes';
import useAudio from '../hooks/useAudio';
import MascotCelebration from './MascotCelebration';
import ScoreBadge from './ScoreBadge';

const normalize = (str) => str?.trim().normalize('NFC');

const SOUND_FILES = {
  correct: 'correct.mp3',
  wrong: 'wrong.mp3',
  fanfare: 'fanfare.mp3',
};

function WordCard({ word }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'WORD',
    item: () => ({ word }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [word]);

  return (
    <div ref={drag} className="word-card" style={{ opacity: isDragging ? 0.5 : 1 }}>
      {word}
    </div>
  );
}

function ImageCard({ item, onDrop }) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'WORD',
    drop: (dragged) => onDrop(dragged.word, item.word),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div ref={drop} className={`image-card ${isOver ? 'hover' : ''}`}>
      <img src={`${process.env.PUBLIC_URL}/images/${item.imageFile}`} alt={item.word} draggable={false} />
    </div>
  );
}

function WordMatching() {
  const [shuffledImages, setShuffledImages] = useState([]);
  const [shuffledWords, setShuffledWords] = useState([]);
  const [matched, setMatched] = useState([]);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [quote, setQuote] = useState('');
  const [confetti, setConfetti] = useState(false);

  const { play } = useAudio();
  const playSound = (type) => play(SOUND_FILES[type], { interrupt: false });

  const extractGameWords = () => {
    const validWords = [];

    alphabetData.forEach(letter => {
      letter.words?.forEach(wordObj => {
        if (wordObj.games?.includes('WordMatching')) {
          validWords.push({
            word: normalize(wordObj.word),
            imageFile: wordObj.imageFile,
          });
        }
      });
    });

    return validWords;
  };

  const initGame = useCallback(() => {
    const allValid = extractGameWords();
    const base = shuffle(allValid).slice(0, 6);

    setShuffledImages(shuffle(base));
    setShuffledWords(shuffle(base));
    setMatched([]);
    setScore(0);
    setMessage('');
    setStartTime(Date.now());
    setEndTime(null);
    setQuote('');
    setConfetti(false);
  }, []);

  useEffect(() => {
    initGame();
  }, [initGame]);

  const handleDrop = (draggedWord, targetWord) => {
    const cleanDragged = normalize(draggedWord);
    const cleanTarget = normalize(targetWord);

    const isCorrect = cleanDragged === cleanTarget;

    setMatched(prev => {
      const alreadyMatched = prev.includes(cleanTarget);

      if (isCorrect && !alreadyMatched) {
        const updated = [...prev, cleanTarget];
        setScore(s => s + 1);
        setMessage(':✅ صحیح!');
        playSound('correct');

        if (updated.length === 6) {
          const finishTime = Date.now();
          setEndTime(finishTime);
          setQuote(getRandomMascotQuote());
          setConfetti(true);
          playSound('fanfare');
        }

        return updated;
      }

      if (!isCorrect) {
        setMessage(':❌ غلط!');
        playSound('wrong');
      }

      return prev;
    });

    setTimeout(() => setMessage(''), 1500);
  };

  const secondsElapsed = endTime
    ? Math.floor((endTime - startTime) / 1000)
    : Math.floor((Date.now() - startTime) / 1000);

  return (
    <div className="wm-container">
     <p>ملاؤ لفظ اور تصویر</p>

      <ScoreBadge score={score} />
      {message && (
        <div className="wm-floating-message">{message}</div>
      )}

      <div className="wm-grid">
        {shuffledWords
          .filter(item => !matched.includes(item.word))
          .map((item) => (
            <WordCard key={item.word + '-word'} word={item.word} />
          ))}
        {shuffledImages
          .filter(item => !matched.includes(item.word))
          .map((item) => (
            <ImageCard key={item.word + '-img'} item={item} onDrop={handleDrop} />
          ))}
      </div>

      {quote && (
        <MascotCelebration quote={quote} onReplay={initGame} />
      )}

      {confetti && <Confetti />}
    </div>
  );
}

export default WordMatching;
